/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AlarmDevice,
  AlarmDevice$inboundSchema,
  AlarmDevice$Outbound,
  AlarmDevice$outboundSchema,
} from "./alarmdevice.js";
import {
  AlarmSnapshot,
  AlarmSnapshot$inboundSchema,
  AlarmSnapshot$Outbound,
  AlarmSnapshot$outboundSchema,
} from "./alarmsnapshot.js";
import {
  UserInfo,
  UserInfo$inboundSchema,
  UserInfo$Outbound,
  UserInfo$outboundSchema,
} from "./userinfo.js";

/**
 * Severity of the alarm.
 */
export const Severity = {
  Critical: "critical",
  Warning: "warning",
  Caution: "caution",
} as const;
/**
 * Severity of the alarm.
 */
export type Severity = OpenEnum<typeof Severity>;

/**
 * Alarm activation information
 */
export type AlarmActivation = {
  /**
   * RFC3339 formatted time in UTC
   */
  acknowledgedAt?: Date | undefined;
  /**
   * User information about a User in the System.
   */
  acknowledgedBy?: UserInfo | undefined;
  /**
   * RFC3339 formatted time in UTC
   */
  activatedAt: Date;
  /**
   * Universally unique identifier
   */
  activationId: string;
  /**
   * Name of the alarm group
   */
  alarmGroup?: string | undefined;
  /**
   * RFC3339 formatted time in UTC
   */
  clearedAt?: Date | undefined;
  /**
   * User information about a User in the System.
   */
  clearedBy?: UserInfo | undefined;
  /**
   * Control points that are relevant to the alarm expression
   */
  controlPoints?: Array<AlarmSnapshot> | undefined;
  /**
   * Metadata for devices associated with the alarm at time of activation
   */
  device?: AlarmDevice | undefined;
  /**
   * Message of the alarm
   */
  message: string;
  /**
   * RFC3339 formatted time in UTC
   */
  resolvedAt?: Date | undefined;
  /**
   * Threshold settings that were exceeded at the moment of activation
   */
  settings?: Array<AlarmSnapshot> | undefined;
  /**
   * Severity of the alarm.
   */
  severity: Severity;
};

/** @internal */
export const Severity$inboundSchema: z.ZodType<
  Severity,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(Severity);
/** @internal */
export const Severity$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  Severity
> = openEnums.outboundSchema(Severity);

/** @internal */
export const AlarmActivation$inboundSchema: z.ZodType<
  AlarmActivation,
  z.ZodTypeDef,
  unknown
> = z.object({
  acknowledged_at: types.optional(types.date()),
  acknowledged_by: types.optional(UserInfo$inboundSchema),
  activated_at: types.date(),
  activation_id: types.string(),
  alarm_group: types.optional(types.string()),
  cleared_at: types.optional(types.date()),
  cleared_by: types.optional(UserInfo$inboundSchema),
  control_points: types.optional(z.array(AlarmSnapshot$inboundSchema)),
  device: types.optional(AlarmDevice$inboundSchema),
  message: types.string(),
  resolved_at: types.optional(types.date()),
  settings: types.optional(z.array(AlarmSnapshot$inboundSchema)),
  severity: Severity$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "acknowledged_at": "acknowledgedAt",
    "acknowledged_by": "acknowledgedBy",
    "activated_at": "activatedAt",
    "activation_id": "activationId",
    "alarm_group": "alarmGroup",
    "cleared_at": "clearedAt",
    "cleared_by": "clearedBy",
    "control_points": "controlPoints",
    "resolved_at": "resolvedAt",
  });
});
/** @internal */
export type AlarmActivation$Outbound = {
  acknowledged_at?: string | undefined;
  acknowledged_by?: UserInfo$Outbound | undefined;
  activated_at: string;
  activation_id: string;
  alarm_group?: string | undefined;
  cleared_at?: string | undefined;
  cleared_by?: UserInfo$Outbound | undefined;
  control_points?: Array<AlarmSnapshot$Outbound> | undefined;
  device?: AlarmDevice$Outbound | undefined;
  message: string;
  resolved_at?: string | undefined;
  settings?: Array<AlarmSnapshot$Outbound> | undefined;
  severity: string;
};

/** @internal */
export const AlarmActivation$outboundSchema: z.ZodType<
  AlarmActivation$Outbound,
  z.ZodTypeDef,
  AlarmActivation
> = z.object({
  acknowledgedAt: z.date().transform(v => v.toISOString()).optional(),
  acknowledgedBy: UserInfo$outboundSchema.optional(),
  activatedAt: z.date().transform(v => v.toISOString()),
  activationId: z.string(),
  alarmGroup: z.string().optional(),
  clearedAt: z.date().transform(v => v.toISOString()).optional(),
  clearedBy: UserInfo$outboundSchema.optional(),
  controlPoints: z.array(AlarmSnapshot$outboundSchema).optional(),
  device: AlarmDevice$outboundSchema.optional(),
  message: z.string(),
  resolvedAt: z.date().transform(v => v.toISOString()).optional(),
  settings: z.array(AlarmSnapshot$outboundSchema).optional(),
  severity: Severity$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    acknowledgedAt: "acknowledged_at",
    acknowledgedBy: "acknowledged_by",
    activatedAt: "activated_at",
    activationId: "activation_id",
    alarmGroup: "alarm_group",
    clearedAt: "cleared_at",
    clearedBy: "cleared_by",
    controlPoints: "control_points",
    resolvedAt: "resolved_at",
  });
});

export function alarmActivationToJSON(
  alarmActivation: AlarmActivation,
): string {
  return JSON.stringify(AlarmActivation$outboundSchema.parse(alarmActivation));
}
export function alarmActivationFromJSON(
  jsonString: string,
): SafeParseResult<AlarmActivation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AlarmActivation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AlarmActivation' from JSON`,
  );
}
